import moment from 'moment';

import {
	PEOPLE_EMPLOYMENT_TYPE,
	PEOPLE_LEAVE_ENTITLEMENT,
	PEOPLE_OPEN_SHIFT_APPLICATION_STATUS,
	PEOPLE_ROSTER_AREA,
	PEOPLE_ROSTER_AREA_NAMES,
	PEOPLE_UNAVAILABILITY_KIND,
	PEOPLE_UNAVAILABILITY_STATUS,
} from '../../constants';
import { normalizePeopleEmploymentType } from './peopleEnums';
import { getPublicHolidayName } from './publicHolidays';
import {
	AVC_MELBOURNE_ID,
	AVC_SYDNEY_ID,
	isTenant,
} from '../user/userTenantCheck';

// Shared roster helpers used by the People web app, the mobile app, and the server.
// Roster days are plain YYYY-MM-DD strings in the employee state's local calendar,
// and shift windows are minutes from midnight, so no timezone conversion is needed.

export const ROSTER_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;
export const DEFAULT_ROSTER_FIRST_DAY_OF_WEEK = 1;
export const SELF_SERVICE_UNAVAILABILITY_NOTICE_DAYS = 7;
export const SELF_SERVICE_UNAVAILABILITY_NOTICE_MESSAGE = 'Unavailability needs at least one week\'s notice. For short-notice dates, contact your manager directly.';
const AUTOMATIC_BREAK_THRESHOLD_MINUTES = 5 * 60;
const AUTOMATIC_BREAK_MINUTES = 30;
const FULL_AND_PART_TIME_LEAVE_TYPES = Object.freeze([
	PEOPLE_LEAVE_ENTITLEMENT.ANNUAL,
	PEOPLE_LEAVE_ENTITLEMENT.SICK,
	PEOPLE_LEAVE_ENTITLEMENT.PARENTAL,
	PEOPLE_LEAVE_ENTITLEMENT.BEREAVEMENT,
	// Paid family and domestic violence leave is an employee entitlement, so it joins the
	// other paid leave types for full-time, part-time and casual staff.
	PEOPLE_LEAVE_ENTITLEMENT.FAMILY_AND_DOMESTIC_VIOLENCE,
]);
// A casual's day off is unavailability, not unpaid leave (unpaid leave has no Xero leave type),
// so casuals keep only their paid family and domestic violence entitlement.
const CASUAL_LEAVE_TYPES = Object.freeze([
	PEOPLE_LEAVE_ENTITLEMENT.FAMILY_AND_DOMESTIC_VIOLENCE,
]);
// Contractors are not employees for paid leave purposes, so they stay on unpaid leave only.
const CONTRACTOR_LEAVE_TYPES = Object.freeze([
	PEOPLE_LEAVE_ENTITLEMENT.UNPAID,
]);
const FULL_AND_PART_TIME_EMPLOYMENT_TYPES = Object.freeze([
	PEOPLE_EMPLOYMENT_TYPE.FULL_TIME,
	PEOPLE_EMPLOYMENT_TYPE.PART_TIME,
]);

export const isKnownPeopleEmploymentType = employmentType => (
	Boolean(normalizePeopleEmploymentType(employmentType))
);

export const getAllowedPeopleLeaveTypes = (employmentType) => {
	switch (normalizePeopleEmploymentType(employmentType)) {
		case PEOPLE_EMPLOYMENT_TYPE.FULL_TIME:
		case PEOPLE_EMPLOYMENT_TYPE.PART_TIME:
			return FULL_AND_PART_TIME_LEAVE_TYPES;
		case PEOPLE_EMPLOYMENT_TYPE.CASUAL:
			return CASUAL_LEAVE_TYPES;
		case PEOPLE_EMPLOYMENT_TYPE.CONTRACTOR:
			return CONTRACTOR_LEAVE_TYPES;
		default:
			return [];
	}
};

export const canEmploymentTypeRequestUnavailability = employmentType => (
	[
		PEOPLE_EMPLOYMENT_TYPE.CASUAL,
		PEOPLE_EMPLOYMENT_TYPE.CONTRACTOR,
	].includes(normalizePeopleEmploymentType(employmentType))
);

// Casuals record time off as unavailability: their unpaid leave has no Xero leave type, so
// payroll had to redo each request. Managers can still record leave for a casual.
export const canEmploymentTypeRequestOwnLeave = employmentType => (
	isKnownPeopleEmploymentType(employmentType) &&
	normalizePeopleEmploymentType(employmentType) !== PEOPLE_EMPLOYMENT_TYPE.CASUAL
);

// Only these categories are safe to name in a notification. Anything else — including a
// sensitive category such as family and domestic violence leave added later — must be
// reported with dates and hours only, because naming the category discloses the reason.
const NON_SENSITIVE_PEOPLE_LEAVE_TYPES = Object.freeze([
	PEOPLE_LEAVE_ENTITLEMENT.ANNUAL,
	PEOPLE_LEAVE_ENTITLEMENT.SICK,
	PEOPLE_LEAVE_ENTITLEMENT.PERSONAL,
	PEOPLE_LEAVE_ENTITLEMENT.UNPAID,
	PEOPLE_LEAVE_ENTITLEMENT.PARENTAL,
	PEOPLE_LEAVE_ENTITLEMENT.BEREAVEMENT,
]);

export const isSensitivePeopleLeaveType = leaveType => (
	!NON_SENSITIVE_PEOPLE_LEAVE_TYPES.includes(Number(leaveType))
);

export const AU_TIMEZONE_BY_STATE = {
	VIC: 'Australia/Melbourne',
	NSW: 'Australia/Sydney',
	QLD: 'Australia/Brisbane',
	WA: 'Australia/Perth',
};

export const isValidRosterDateString = (value) => {
	if (typeof value !== 'string' || !ROSTER_DATE_REGEX.test(value)) {
		return false;
	}

	const [year, month, day] = value.split('-').map(Number);
	const date = new Date(Date.UTC(year, month - 1, day));

	return date.getUTCFullYear() === year &&
		date.getUTCMonth() === month - 1 &&
		date.getUTCDate() === day;
};

export const rosterDateToUtc = (dateString) => {
	const [year, month, day] = dateString.split('-').map(Number);
	return new Date(Date.UTC(year, month - 1, day));
};

const pad2 = value => String(value).padStart(2, '0');

export const utcToRosterDate = (date) => {
	return `${date.getUTCFullYear()}-${pad2(date.getUTCMonth() + 1)}-${pad2(date.getUTCDate())}`;
};

export const addDaysToRosterDate = (dateString, days) => {
	const date = rosterDateToUtc(dateString);
	date.setUTCDate(date.getUTCDate() + days);
	return utcToRosterDate(date);
};

export const getRosterWeekday = dateString => rosterDateToUtc(dateString).getUTCDay();

export const normalizeRosterFirstDayOfWeek = (value) => {
	if (value === null || value === undefined || value === '') {
		return DEFAULT_ROSTER_FIRST_DAY_OF_WEEK;
	}

	const weekday = Number(value);
	return Number.isInteger(weekday) && weekday >= 0 && weekday <= 6
		? weekday
		: DEFAULT_ROSTER_FIRST_DAY_OF_WEEK;
};

// YYYY-MM-DD strings compare correctly as plain strings.
export const compareRosterDates = (a, b) => String(a).localeCompare(String(b));

export const getRosterDateRangeLength = (fromDate, toDate) => {
	const diffMs = rosterDateToUtc(toDate).getTime() - rosterDateToUtc(fromDate).getTime();
	return Math.round(diffMs / (24 * 60 * 60 * 1000)) + 1;
};

export const enumerateRosterDates = (fromDate, toDate) => {
	const dates = [];
	let current = fromDate;
	while (compareRosterDates(current, toDate) <= 0) {
		dates.push(current);
		current = addDaysToRosterDate(current, 1);
	}
	return dates;
};

// Full-time and part-time leave covers only the employee's normal working days.
// Casual and contractor leave intentionally keeps covering every calendar day.
export const isFullOrPartTimeEmploymentType = employmentType => (
	FULL_AND_PART_TIME_EMPLOYMENT_TYPES.includes(normalizePeopleEmploymentType(employmentType))
);

// Casual staff have no paid annual, sick or personal leave (only family and domestic violence
// leave is paid for them) and contractors are not on payroll, so their other leave is never paid
// leave for payroll or Xero. An unknown employment type keeps the leave type's own meaning.
export const isPaidLeaveTypeForEmployee = ({ leaveType, employmentType }) => {
	if (Number(leaveType) === PEOPLE_LEAVE_ENTITLEMENT.UNPAID) {
		return false;
	}

	const normalizedType = normalizePeopleEmploymentType(employmentType);
	if (normalizedType === PEOPLE_EMPLOYMENT_TYPE.CONTRACTOR) {
		return false;
	}
	if (normalizedType === PEOPLE_EMPLOYMENT_TYPE.CASUAL) {
		return Number(leaveType) === PEOPLE_LEAVE_ENTITLEMENT.FAMILY_AND_DOMESTIC_VIOLENCE;
	}

	return true;
};

// Falls back to Monday-Friday when the employee has no valid working-day pattern,
// matching the labour-efficiency scheduled-minutes fallback.
export const getEmployeeWorkDays = (workDays) => {
	const rawWorkDays = Array.isArray(workDays) ? workDays.map(Number) : [];
	const uniqueWorkDays = new Set(rawWorkDays);
	const workDaysAreValid = rawWorkDays.length > 0 &&
		uniqueWorkDays.size === rawWorkDays.length &&
		rawWorkDays.every(day => Number.isInteger(day) && day >= 0 && day <= 6);

	return workDaysAreValid
		? [...rawWorkDays].sort((left, right) => left - right)
		: [1, 2, 3, 4, 5];
};

// A normal working day is a weekday the employee works that is not a public holiday in
// their state. `stateCode` comes from the same source the roster uses for holiday cells,
// so leaving off an unmapped state excludes weekends only and never guesses a holiday.
export const isEmployeeLeaveWorkingDay = ({ date, workDays, stateCode }) => {
	if (!isValidRosterDateString(date)) {
		return false;
	}

	if (!getEmployeeWorkDays(workDays).includes(getRosterWeekday(date))) {
		return false;
	}

	return !getPublicHolidayName(date, stateCode);
};

export const buildEmployeeLeaveWorkingDates = ({ startDate, endDate, workDays, stateCode }) => {
	if (!isValidRosterDateString(startDate) ||
		!isValidRosterDateString(endDate) ||
		compareRosterDates(startDate, endDate) > 0) {
		return [];
	}

	return enumerateRosterDates(startDate, endDate).filter(date => (
		isEmployeeLeaveWorkingDay({ date, workDays, stateCode })
	));
};

export const buildRosterRepeatDates = ({
	sourceDate,
	weekdays,
	weeks,
	includeSourceDate = false,
}) => {
	const repeatWeeks = Number(weeks);
	const selectedWeekdays = new Set((weekdays || [])
		.map(Number)
		.filter(day => Number.isInteger(day) && day >= 0 && day <= 6));

	if (!isValidRosterDateString(sourceDate) ||
		!Number.isInteger(repeatWeeks) ||
		repeatWeeks < 1 ||
		!selectedWeekdays.size) {
		return [];
	}

	const startDate = includeSourceDate ? sourceDate : addDaysToRosterDate(sourceDate, 1);
	const endDate = includeSourceDate
		? addDaysToRosterDate(getRosterWeekStart(sourceDate), (repeatWeeks * 7) - 1)
		// Copy mode is forward-looking so Sunday-to-Monday defaults still produce a target.
		: addDaysToRosterDate(sourceDate, repeatWeeks * 7);
	const dates = [];
	let current = startDate;

	while (compareRosterDates(current, endDate) <= 0) {
		if (selectedWeekdays.has(getRosterWeekday(current))) {
			dates.push(current);
		}
		current = addDaysToRosterDate(current, 1);
	}

	return dates;
};

export const buildRosterRecurringDates = ({
	sourceDate,
	repeatUntilDate,
	weekdays,
	intervalWeeks = 1,
}) => {
	const selectedWeekdays = new Set((weekdays || [])
		.map(Number)
		.filter(day => Number.isInteger(day) && day >= 0 && day <= 6));
	const repeatIntervalWeeks = Number(intervalWeeks);

	if (!isValidRosterDateString(sourceDate) ||
		!isValidRosterDateString(repeatUntilDate) ||
		compareRosterDates(repeatUntilDate, sourceDate) < 0 ||
		!Number.isInteger(repeatIntervalWeeks) ||
		repeatIntervalWeeks < 1 ||
		!selectedWeekdays.size) {
		return [];
	}

	const sourceWeekStart = getRosterWeekStart(sourceDate);
	const dates = [];
	let current = sourceDate;

	while (compareRosterDates(current, repeatUntilDate) <= 0) {
		const weeksFromSource = Math.floor((getRosterDateRangeLength(sourceWeekStart, getRosterWeekStart(current)) - 1) / 7);

		if (weeksFromSource % repeatIntervalWeeks === 0 &&
			selectedWeekdays.has(getRosterWeekday(current))) {
			dates.push(current);
		}

		current = addDaysToRosterDate(current, 1);
	}

	return dates;
};

// Today's date string in the given AU state's timezone (defaults to Sydney).
export const getTodayRosterDate = (stateCode) => {
	const timeZone = AU_TIMEZONE_BY_STATE[stateCode] || AU_TIMEZONE_BY_STATE.NSW;
	return getTodayRosterDateForTimezone(timeZone);
};

export const getTodayRosterDateForTimezone = (timeZone) => {
	const resolvedTimeZone = timeZone || AU_TIMEZONE_BY_STATE.NSW;
	// en-CA formats as YYYY-MM-DD.
	try {
		return new Intl.DateTimeFormat('en-CA', { timeZone: resolvedTimeZone }).format(new Date());
	}
	catch {
		return new Intl.DateTimeFormat('en-CA', { timeZone: AU_TIMEZONE_BY_STATE.NSW }).format(new Date());
	}
};

// Today's date string in the device's own timezone — the right "today" for self-service
// screens where the person is physically in their state.
export const getTodayDeviceRosterDate = () => {
	return moment().format('YYYY-MM-DD');
};

export const formatRosterDate = (dateString, format = 'ddd D MMM YYYY') => {
	return dateString ? moment(dateString, 'YYYY-MM-DD').format(format) : '';
};

export const formatRosterDateRange = (startDate, endDate, format) => {
	if (startDate === endDate) {
		return formatRosterDate(startDate, format);
	}
	return `${formatRosterDate(startDate, format)} – ${formatRosterDate(endDate, format)}`;
};

export const getSelfServiceUnavailabilityCutoffDate = todayDate => (
	addDaysToRosterDate(todayDate, SELF_SERVICE_UNAVAILABILITY_NOTICE_DAYS)
);

export const isSelfServiceUnavailabilityShortNotice = ({ kind, startDate, todayDate }) => {
	if (Number(kind) !== PEOPLE_UNAVAILABILITY_KIND.UNAVAILABLE ||
		!isValidRosterDateString(startDate) ||
		!isValidRosterDateString(todayDate)) {
		return false;
	}

	return compareRosterDates(startDate, getSelfServiceUnavailabilityCutoffDate(todayDate)) < 0;
};

// Self-service default view: upcoming entries plus anything still awaiting a decision,
// so old history cannot bury current requests and past pending leave stays actionable
// (employees can still edit or withdraw it regardless of its dates).
export const isUpcomingOrPendingUnavailability = (entry, todayDate) => (
	Number(entry?.status) === PEOPLE_UNAVAILABILITY_STATUS.PENDING ||
	compareRosterDates(entry?.endDate || entry?.startDate || '', todayDate) >= 0
);

// Blueprint intent names shared by the web list and My account tab.
export const getUnavailabilityStatusIntent = (status) => {
	switch (Number(status)) {
		case PEOPLE_UNAVAILABILITY_STATUS.APPROVED:
			return 'success';
		case PEOPLE_UNAVAILABILITY_STATUS.PENDING:
			return 'warning';
		case PEOPLE_UNAVAILABILITY_STATUS.DECLINED:
			return 'danger';
		default:
			return 'none';
	}
};

export const getOpenShiftApplicationStatusIntent = (status) => {
	switch (Number(status)) {
		case PEOPLE_OPEN_SHIFT_APPLICATION_STATUS.ACCEPTED:
			return 'success';
		case PEOPLE_OPEN_SHIFT_APPLICATION_STATUS.PENDING:
			return 'warning';
		case PEOPLE_OPEN_SHIFT_APPLICATION_STATUS.DECLINED:
			return 'danger';
		default:
			return 'none';
	}
};

// Mirrors the server self-service change rules: leave must still be pending, while
// unavailability remains editable so employees can adjust or withdraw availability requests.
const canEmployeeChangeOwnUnavailability = (entry) => {
	if (!entry) {
		return false;
	}

	return Number(entry.kind) !== PEOPLE_UNAVAILABILITY_KIND.LEAVE ||
		Number(entry.status) === PEOPLE_UNAVAILABILITY_STATUS.PENDING;
};

export const canEmployeeEditOwnUnavailability = canEmployeeChangeOwnUnavailability;
export const canEmployeeRemoveOwnUnavailability = canEmployeeChangeOwnUnavailability;

// Start of the roster week containing the given date; rosters default to Monday-based weeks.
export const getRosterWeekStart = (dateString, firstDayOfWeek = DEFAULT_ROSTER_FIRST_DAY_OF_WEEK) => {
	const date = rosterDateToUtc(dateString);
	const weekday = date.getUTCDay(); // 0 = Sunday
	const startWeekday = normalizeRosterFirstDayOfWeek(firstDayOfWeek);
	const daysSinceWeekStart = (weekday - startWeekday + 7) % 7;
	return addDaysToRosterDate(dateString, -daysSinceWeekStart);
};

export const formatShiftMinutes = (minutes) => {
	const total = Number(minutes) || 0;
	const hours24 = Math.floor(total / 60) % 24;
	const mins = total % 60;
	const period = hours24 >= 12 ? 'pm' : 'am';
	const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12;
	return mins ? `${hours12}:${pad2(mins)}${period}` : `${hours12}${period}`;
};

export const formatShiftRange = (startMinutes, endMinutes) => {
	return `${formatShiftMinutes(startMinutes)} – ${formatShiftMinutes(endMinutes)}`;
};

// Hide Deputy source suffixes and the roster marker retained in legacy leave notes.
const SICK_DAY_SOURCE_NOTE_PATTERN = /\s*\[(?:Marked sick from roster|Imported from Deputy|Deputy leave:[^\]]+)\]\s*$/i;

export const normalizeSickDayReason = value => String(value || '')
	.replace(SICK_DAY_SOURCE_NOTE_PATTERN, '')
	.trim();

const getTimesheetOverlapMinutes = (timesheet, shift) => {
	const start = Math.max(Number(timesheet?.startMinutes), Number(shift?.startMinutes));
	const end = Math.min(Number(timesheet?.endMinutes), Number(shift?.endMinutes));
	const overlap = end - start;
	return Number.isFinite(overlap) && overlap > 0 ? overlap : 0;
};

// Timesheet carrying a shift's actual worked time: the explicitly linked one, else the best overlap.
export const getRosterShiftTimesheet = (shift, actualRow) => {
	const timesheets = actualRow?.timesheets || [];
	if (!shift || !timesheets.length) {
		return null;
	}

	const linkedTimesheet = timesheets.find(timesheet => timesheet.rosterShift?.id === shift.id);
	if (linkedTimesheet) {
		return linkedTimesheet;
	}

	return timesheets
		.map(timesheet => ({
			timesheet,
			overlapMinutes: getTimesheetOverlapMinutes(timesheet, shift),
		}))
		.filter(item => item.overlapMinutes > 0)
		.sort((left, right) => right.overlapMinutes - left.overlapMinutes)[0]?.timesheet || null;
};

const DRIVER_DEFAULT_SHIFT = {
	startMinutes: 7 * 60,
	endMinutes: 15 * 60,
	label: 'driver default',
};

const MELBOURNE_PICKER_DEFAULT_SHIFT = {
	startMinutes: 12 * 60,
	endMinutes: (18 * 60) + 30,
	label: 'Melbourne picker default',
};

const SYDNEY_PICKER_DEFAULT_SHIFT = {
	startMinutes: 10 * 60,
	endMinutes: 16 * 60,
	label: 'Sydney picker default',
};

const normalizeRosterLabel = value => String(value || '').trim().toLowerCase();

const isRosterWorkTenant = (workTenant, tenantId, tenantName) => (
	isTenant(workTenant, tenantId) ||
	normalizeRosterLabel(workTenant?.name) === normalizeRosterLabel(tenantName)
);

// AVC's standard shift windows follow the employee's operational role and work state.
// Open shifts have no employee yet, so their Driver/Picker area supplies the role.
export const getDefaultRosterShiftWindow = ({
	employee = null,
	position = null,
	workTenant = null,
	area = null,
	areaLabel = '',
} = {}) => {
	const resolvedPosition = position || employee?.position;
	const resolvedWorkTenant = workTenant || employee?.workTenant;
	const positionName = normalizeRosterLabel(resolvedPosition?.name || resolvedPosition);
	const numericArea = Number(area);
	const normalizedAreaLabel = normalizeRosterLabel(areaLabel);
	const isDriver = positionName.includes('driver') ||
		(!positionName && (
			numericArea === PEOPLE_ROSTER_AREA.DRIVER ||
			numericArea === PEOPLE_ROSTER_AREA.DRIVER_TRAINING ||
			normalizedAreaLabel.includes('driver')
		));
	const isPicker = positionName.includes('picker') ||
		(!positionName && (
			numericArea === PEOPLE_ROSTER_AREA.PICKER ||
			normalizedAreaLabel.includes('picker')
		));

	if (isDriver) {
		return DRIVER_DEFAULT_SHIFT;
	}
	if (!isPicker) {
		return null;
	}
	if (isRosterWorkTenant(resolvedWorkTenant, AVC_MELBOURNE_ID, 'Melbourne')) {
		return MELBOURNE_PICKER_DEFAULT_SHIFT;
	}
	if (isRosterWorkTenant(resolvedWorkTenant, AVC_SYDNEY_ID, 'Sydney')) {
		return SYDNEY_PICKER_DEFAULT_SHIFT;
	}

	return null;
};

export const rosterShiftMatchesDefaultWindow = ({
	startMinutes,
	endMinutes,
	defaultWindow,
}) => (
	Boolean(defaultWindow) &&
	Number(startMinutes) === Number(defaultWindow.startMinutes) &&
	Number(endMinutes) === Number(defaultWindow.endMinutes)
);

export const getShiftDurationHours = (startMinutes, endMinutes) => {
	const duration = (Number(endMinutes) || 0) - (Number(startMinutes) || 0);
	return duration > 0 ? duration / 60 : 0;
};

export const getAutomaticShiftBreakMinutes = (startMinutes, endMinutes) => {
	const start = Number(startMinutes);
	const end = Number(endMinutes);
	if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) {
		return 0;
	}

	return end - start > AUTOMATIC_BREAK_THRESHOLD_MINUTES
		? AUTOMATIC_BREAK_MINUTES
		: 0;
};

export const formatDurationMinutes = (minutes, emptyValue = '') => {
	const numericMinutes = Number(minutes);
	if (!Number.isFinite(numericMinutes)) {
		return emptyValue;
	}

	const roundedMinutes = Math.round(Math.abs(numericMinutes));
	const sign = numericMinutes < 0 && roundedMinutes ? '-' : '';
	const hours = Math.floor(roundedMinutes / 60);
	const remainingMinutes = roundedMinutes % 60;

	if (!hours) {
		return `${sign}${remainingMinutes}min`;
	}
	if (!remainingMinutes) {
		return `${sign}${hours}h`;
	}
	return `${sign}${hours}h ${remainingMinutes}min`;
};

export const formatDurationHours = (hours, emptyValue = '') => {
	const numericHours = Number(hours);
	if (!Number.isFinite(numericHours)) {
		return emptyValue;
	}

	return formatDurationMinutes(numericHours * 60, emptyValue);
};

// Options for time selects in configurable steps, defaulting to roster 15-minute increments.
export const buildShiftTimeOptions = ({ fromMinutes = 0, toMinutes = 24 * 60, stepMinutes = 15 } = {}) => {
	const options = [];
	for (let minutes = fromMinutes; minutes <= toMinutes; minutes += stepMinutes) {
		// Label the end-of-day option distinctly so it is not confused with 12am at start of day.
		options.push({ value: minutes, label: minutes === 24 * 60 ? 'Midnight' : formatShiftMinutes(minutes) });
	}
	return options;
};

// Start times stop before end of day; end times start after midnight (shifts cannot cross
// midnight — overnight work is entered as two shifts split at midnight).
export const buildShiftStartTimeOptions = (stepMinutes = 15) => buildShiftTimeOptions({
	toMinutes: (24 * 60) - stepMinutes,
	stepMinutes,
});
export const buildShiftEndTimeOptions = (stepMinutes = 15) => buildShiftTimeOptions({
	fromMinutes: stepMinutes,
	stepMinutes,
});

// Shift chip colours per area (published shifts). Drafts and sick days override these in the UI.
const ROSTER_AREA_COLORS = {
	[PEOPLE_ROSTER_AREA.DRIVER]: { background: '#2d72d2', color: '#ffffff' },
	[PEOPLE_ROSTER_AREA.PICKER]: { background: '#238551', color: '#ffffff' },
	[PEOPLE_ROSTER_AREA.DRIVER_TRAINING]: { background: '#c87619', color: '#ffffff' },
	[PEOPLE_ROSTER_AREA.WAREHOUSE]: { background: '#7961db', color: '#ffffff' },
	[PEOPLE_ROSTER_AREA.OFFICE]: { background: '#00a396', color: '#ffffff' },
	[PEOPLE_ROSTER_AREA.WORKING_FROM_HOME]: { background: '#0f6894', color: '#ffffff' },
	[PEOPLE_ROSTER_AREA.OTHER]: { background: '#8f99a8', color: '#ffffff' },
};

const ROSTER_AREA_DEFAULT_COLORS = { background: '#8f99a8', color: '#ffffff' };

export const getRosterAreaColors = (area) => {
	return ROSTER_AREA_COLORS[Number(area)] || ROSTER_AREA_DEFAULT_COLORS;
};

const EMPLOYEE_WORK_STATUS_AREA_LABELS = {
	[PEOPLE_ROSTER_AREA.OFFICE]: 'In office',
	[PEOPLE_ROSTER_AREA.WAREHOUSE]: 'In warehouse',
	[PEOPLE_ROSTER_AREA.WORKING_FROM_HOME]: 'Working from home',
	[PEOPLE_ROSTER_AREA.OTHER]: 'Out of office, working',
};

export const getEmployeeWorkStatusLabel = (entry) => {
	const area = Number(entry?.area);
	if (area === PEOPLE_ROSTER_AREA.WORKING_FROM_HOME) {
		const status = Number(entry?.status);
		if (status === PEOPLE_UNAVAILABILITY_STATUS.PENDING) {
			return 'WFH - pending approval';
		}
		if (status === PEOPLE_UNAVAILABILITY_STATUS.DECLINED) {
			return 'WFH - declined';
		}
	}

	return EMPLOYEE_WORK_STATUS_AREA_LABELS[area] || PEOPLE_ROSTER_AREA_NAMES[area] || '';
};
